// src/pages/shop/ShopReport.jsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { reports as reportsApi } from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useToast } from '../../hooks/useToast';
import { SHOP_NAV, useShopNav } from './ShopHome';
import { generateReportPDF } from '../../utils/generateReportPDF';
import { savePDF } from '../../utils/savePDF';

function fmtMoney(n) { return Math.round(n).toLocaleString('ru'); }

// Пресеты периода
const PRESETS = [
  { id: 'today',     labelKey: "today", days: 1 },
  { id: 'week',      labelKey: "week",  days: 7 },
  { id: 'month',     labelKey: "month",   days: 30 },
  { id: 'quarter',   labelKey: "quarter", days: 90 },
  { id: 'year',      labelKey: "year",     days: 365 },
  { id: 'custom',    labelKey: "custom",    days: null },
];

function todayISODate() {
  const d = new Date();
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}

function daysAgoISODate(n) {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60_000).toISOString().slice(0, 10);
}

export default function ShopReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const navItems = useShopNav();
  const currency = useMyCurrency();

  const [presetId, setPresetId] = useState('month');
  const [customFrom, setCustomFrom] = useState(daysAgoISODate(30));
  const [customTo, setCustomTo]     = useState(todayISODate());

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const buildQuery = () => {
    if (presetId === 'custom') {
      const fromDate = new Date(customFrom + 'T00:00:00');
      const toDate   = new Date(customTo + 'T23:59:59');
      return { from: fromDate.toISOString(), to: toDate.toISOString() };
    }
    const preset = PRESETS.find(p => p.id === presetId);
    return { days: preset.days };
  };

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const data = await reportsApi.inventory(buildQuery());
      setPreview(data);
    } catch (e) {
      show(e.message, 'error');
    }
    setLoading(false);
  };

  const downloadPDF = async () => {
    if (!preview) {
      // Если предпросмотра ещё нет — сначала загружаем
      await loadPreview();
    }
    setGenerating(true);
    try {
      const data = preview || await reportsApi.inventory(buildQuery());
      const blob = await generateReportPDF(data);
      const fromDate = new Date(data.period.from);
      const toDate   = new Date(data.period.to);
      // Добавляем timestamp в имя файла, чтобы повторное сохранение
      // в тот же день за тот же период создавало новый файл, а не конфликтовало.
      const now = new Date();
      const stamp = `${now.getHours().toString().padStart(2,'0')}-${now.getMinutes().toString().padStart(2,'0')}-${now.getSeconds().toString().padStart(2,'0')}`;
      // Имя файла: {ReportTitle}_{ShopName}_{from}_{to}_{HHMMSS}.pdf
      // Заменяем недопустимые символы на _, чтобы имя было валидным для FS на Android.
      const safeTitle = t('report.title').replace(/[^a-zA-Zа-яА-ЯЁё0-9 ]/g, '_');
      const safeShop = data.shop.name.replace(/[^a-zA-Zа-яА-ЯЁё0-9 ]/g, '_');
      const filename = `${safeTitle}_${safeShop}_${fromDate.toISOString().slice(0, 10)}_${toDate.toISOString().slice(0, 10)}_${stamp}.pdf`;

      const result = await savePDF(blob, filename);
      if (result.method === 'filesystem') {
        show(t('report.savedToDocuments'));
      } else if (result.method === 'share') {
        show(t('report.shareChoose'));
      } else {
        show(t('report.downloaded'));
      }
    } catch (e) {
      console.error(e);
      show(e.message || t('common.error'), 'error');
    }
    setGenerating(false);
  };

  return (
    <div className="page">
      {ToastView}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/analytics')}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('report.subtitle')}</p>
          <h1 className="page-title">{t('report.title')}</h1>
        </div>
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Выбор периода */}
        <div className="card">
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em', marginBottom: 10 }}>
            {t("report.period")}
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 6,
          }}>
            {PRESETS.map(p => (
              <button
                key={p.id}
                onClick={() => setPresetId(p.id)}
                style={{
                  padding: '10px 6px',
                  background: presetId === p.id ? 'var(--brand-primary)' : 'var(--bg-subtle)',
                  color: presetId === p.id ? '#fff' : 'var(--fg)',
                  border: 'none',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', fontFamily: 'var(--font-display)',
                }}
              >
                {t(`common.${p.labelKey}`)}
              </button>
            ))}
          </div>

          {presetId === 'custom' && (
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label>{t("common.from")}</label>
                <input
                  type="date"
                  className="input"
                  value={customFrom}
                  max={customTo}
                  onChange={e => setCustomFrom(e.target.value)}
                />
              </div>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label>{t("common.to")}</label>
                <input
                  type="date"
                  className="input"
                  value={customTo}
                  min={customFrom}
                  max={todayISODate()}
                  onChange={e => setCustomTo(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Действия */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-secondary"
            onClick={loadPreview}
            disabled={loading || generating}
            style={{ flex: 1 }}
          >
            {loading ? t('common.loading') : t('common.preview')}
          </button>
          <button
            className="btn btn-primary"
            onClick={downloadPDF}
            disabled={generating || loading}
            style={{ flex: 2 }}
          >
            {generating ? t('report.creating') : <><Icon name="package" size={16} stroke={2} /> {t("report.downloadPDF")}</>}
          </button>
        </div>

        {/* Предпросмотр сводки */}
        {preview && (
          <>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em', marginTop: 8 }}>
              {t('report.previewTitle')} · {new Date(preview.period.from).toLocaleDateString()} – {new Date(preview.period.to).toLocaleDateString()}
            </p>

            <div className="card">
              <SummaryRow label={t("report.revenueCash")} value={formatMoney(preview.summary.revenueCash, currency)} />
              <SummaryRow label={t("report.revenuePoints")}   value={formatMoney(preview.summary.revenuePoints, currency)} accent />
              {preview.summary.cashTopUp > 0 && (
                <SummaryRow label={t("report.cashTopUp")} value={formatMoney(preview.summary.cashTopUp, currency)} muted />
              )}
              {preview.summary.pointsRedeemed > 0 && (
                <SummaryRow label={t("report.pointsRedeemedShort")} value={`−${preview.summary.pointsRedeemed.toLocaleString()} ⭐`} muted />
              )}
              <SummaryRow label={t("analytics.profit")} value={formatMoney(preview.summary.profit, currency)} positive />
              <SummaryRow label={t("report.salesShort")} value={`${preview.summary.salesCount}`} muted />
              <Hr />
              <SummaryRow label={t("report.purchasesShort")}  value={formatMoney(preview.summary.purchaseTotal, currency)} />
              <SummaryRow label={t("report.returnsShort")} value={formatMoney(preview.summary.returnTotal, currency)} />
              <SummaryRow label={t("report.writeoffsShort")}   value={formatMoney(preview.summary.writeoffTotal, currency)} negative />
            </div>

            <div className="card">
              <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t("report.willInclude")}</p>
              <ul style={{
                listStyle: 'none', padding: 0, margin: 0,
                display: 'flex', flexDirection: 'column', gap: 6,
                fontSize: 13, color: 'var(--fg-muted)',
              }}>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t("report.productsRow")}</span>
                  <strong style={{ color: 'var(--fg)' }}>{preview.products.length}</strong>
                </li>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t("report.purchasesRow")}</span>
                  <strong style={{ color: 'var(--fg)' }}>{preview.purchases.length}</strong>
                </li>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t("report.writeoffsRow")}</span>
                  <strong style={{ color: 'var(--fg)' }}>{preview.writeoffs.length}</strong>
                </li>
                <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t("report.returnsRow")}</span>
                  <strong style={{ color: 'var(--fg)' }}>{preview.returns.length}</strong>
                </li>
              </ul>
            </div>
          </>
        )}

        <div style={{ height: 24 }} />
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}

function SummaryRow({ label, value, accent, positive, negative, muted }) {
  let valueColor = 'var(--fg)';
  if (accent)   valueColor = 'var(--brand-accent)';
  if (positive) valueColor = 'var(--success)';
  if (negative) valueColor = 'var(--danger)';
  if (muted)    valueColor = 'var(--fg-muted)';

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0',
    }}>
      <span style={{ fontSize: 13, color: muted ? 'var(--fg-faint)' : 'var(--fg-muted)' }}>{label}</span>
      <span style={{
        fontSize: 14, fontWeight: 700,
        color: valueColor,
        fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)',
      }}>
        {value}
      </span>
    </div>
  );
}

function Hr() {
  return <div style={{ height: 1, background: 'var(--border-subtle)', margin: '8px 0' }} />;
}
